import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, Observable, Subscription, distinctUntilChanged, take } from 'rxjs';
import { selectTabList } from '../../../state/selectors';
import { AppState, StoredTabData, Tab } from '../../../state/app.state';
import { logout, replaceAllTabs, setActiveTab, changeActiveTab } from '../../../state/action';
import { TabsService } from '../../../features/results/services/tabs.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-main-navbar',
  standalone: true,
  imports: [RouterOutlet, CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './main-navbar.component.html',
  styleUrls: ['./main-navbar.component.css'],
})
export class MainNavbarComponent implements OnInit, OnDestroy {
  hasTabs$: Observable<boolean>;
  roles: string | null = null;
  isAuthenticated: boolean = false;
  private subscriptions = new Subscription();
  private previousAuthState: boolean | null = null;
  private isLoadingTabs = false;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private tabsService: TabsService,
    private authService: AuthService
  ) {
    this.hasTabs$ = this.store.select(selectTabList).pipe(
      map((tabs: Tab[]) => tabs && tabs.length > 0)
    );
  }

  ngOnInit(): void {
    this.updateAuthState();
    this.previousAuthState = this.isAuthenticated;
    
    if (this.isAuthenticated) {
      this.loadTabsIfStoreEmpty();
    }
    
    this.subscriptions.add(
      this.authService.authenticated.pipe(
        distinctUntilChanged()
      ).subscribe((isAuth) => {
        if (this.isAuthenticated !== this.previousAuthState) {
          if (this.isAuthenticated && !this.previousAuthState) {
            this.tabsService.clearCache();
            this.loadTabsIfStoreEmpty();
          } else if (!this.isAuthenticated && this.previousAuthState) {
            this.tabsService.clearCache();
            this.store.dispatch(replaceAllTabs({ tabs: [] }));
          }
          this.previousAuthState = this.isAuthenticated;
        }
        this.updateAuthState();
      })
    );
  }

  private updateAuthState(): void {
    this.roles = JSON.parse(localStorage.getItem('roles') || 'null');
    this.isAuthenticated = !!localStorage.getItem('current_user_token');
  }

  private loadTabsIfStoreEmpty(): void {
    if (!this.isAuthenticated || this.isLoadingTabs || this.tabsService.hasLoadedTabsOnce()) {
      return;
    }

    this.isLoadingTabs = true;
    setTimeout(() => {
      this.store.select(selectTabList).pipe(take(1)).subscribe(currentTabs => {
        if (currentTabs.length > 0) {
          this.tabsService.setHasLoadedOnce();
          this.isLoadingTabs = false;
          return;
        }
        
        this.subscriptions.add(
          this.tabsService.load().subscribe({
            next: (tabs) => {
              this.isLoadingTabs = false;
              this.store.select(selectTabList).pipe(take(1)).subscribe(existingTabs => {
                if (existingTabs.length === 0 && tabs?.length > 0) {
                  const restored: Tab[] = tabs.map((t: StoredTabData) => ({
                    ...t,
                    sidenav_results: {},
                    isLoading: false
                  }));
                  this.store.dispatch(replaceAllTabs({ tabs: restored }));
                  if (restored[0]) {
                    this.store.dispatch(setActiveTab({ tab: restored[0] }));
                    this.store.dispatch(changeActiveTab({ tabId: restored[0].tabId }));
                  }
                }
              });
            },
            error: (err) => {
              this.isLoadingTabs = false;
              console.warn('Tabs could not be loaded:', err);
            }
          })
        );
      });
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  logout(): void {
    this.tabsService.clearCache();
    this.previousAuthState = false;
    this.store.dispatch(logout());
    localStorage.removeItem("current_user_token");
    localStorage.removeItem("timestamp");
    localStorage.removeItem("user");
    localStorage.removeItem("roles");
    localStorage.removeItem("allow_sampling");
    this.router.navigate(['/login']);
  }
}